import {ContentTileWithTitleAndToggleSeparated} from '#/atoms/content-tile';
import {VerticalSeparator} from '#/atoms/vertical-separator';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuContentSectionInfoRow} from '#/components/actionmenu/subcomponents';
import {useDemographics} from '#/hooks/query/use-demographics';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {DefaultChildren} from '#/utils/utils';
import {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

export const ActionMenuContentSectionDemographics: FunctionComponent<DefaultChildren> = () => {
  const {t: tRaw} = useTranslation('translation');
  const {t} = useTranslation('translation', {
    keyPrefix: 'pages.actionMenu.information.sections.information.contentSection.demographics',
  });

  const displayPref = useCoinDisplayPreferences();

  // Context
  const {land} = useContext(ActionMenuContext);

  // External hooks
  const {data: demographics} = useDemographics(land.tokenSymbol);

  return (
    <ContentTileWithTitleAndToggleSeparated title={t('title')} contentClassName='text-white space-y-2'>
      {{
        opened: (
          <>
            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianHomeSalePrice.title'),
                right: demographics?.medianHomeSalePrice
                  ? t('infoRows.medianHomeSalePrice.value', {
                      currencySymbol: displayPref.symbol,
                      amount: displayPref.displayAmount(demographics.medianHomeSalePrice * 1_000_000),
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>
            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianHomeSalePerMonth.title'),
                right: demographics?.medianSalesByMonth
                  ? t('infoRows.medianHomeSalePerMonth.value', {
                      value: demographics.medianSalesByMonth,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.population.title'),
                right: demographics?.population
                  ? t('infoRows.population.value', {
                      value: demographics.population,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianPopulationAge.title'),
                right: demographics?.medianAge
                  ? t('infoRows.medianPopulationAge.value', {
                      value: demographics.medianAge,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianHouseholdIncome.title'),
                right: demographics?.medianHouseholdIncome
                  ? t('infoRows.medianHouseholdIncome.value', {
                      currencySymbol: displayPref.symbol,
                      amount: displayPref.displayAmount(demographics.medianHouseholdIncome * 1_000_000),
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>
          </>
        ),
        toOpen: (
          <>
            <VerticalSeparator />

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianHouseSize.title'),
                right: demographics?.observation
                  ? t('infoRows.medianHouseSize.value', {
                      value: demographics.observation.houseSizeSquareFoot,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianLotSize.title'),
                right: demographics?.observation
                  ? t('infoRows.medianLotSize.value', {
                      value: demographics.observation.lotSizeSquareFoot,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianConstructionAge.title'),
                right: demographics?.observation
                  ? t('infoRows.medianConstructionAge.value', {
                      value: demographics.observation.age,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianNumberOfBedrooms.title'),
                right: demographics?.observation
                  ? t('infoRows.medianNumberOfBedrooms.value', {
                      value: demographics.observation.bedrooms,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.medianNumberOfBathrooms.title'),
                right: demographics?.observation
                  ? t('infoRows.medianNumberOfBathrooms.value', {
                      value: demographics.observation.bathrooms,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.mostPopularZipCode.title'),
                right: demographics?.observation
                  ? t('infoRows.mostPopularZipCode.value', {
                      value: demographics.observation.zipCode,
                    })
                  : tRaw('pages.actionMenu.components.contentSection.infoRow.empty'),
              }}
            </ActionMenuContentSectionInfoRow>
          </>
        ),
      }}
    </ContentTileWithTitleAndToggleSeparated>
  );
};
